import type { ProductPromo } from '@virginexperiencedays/promos';

export type ProductMapType = { [slug: string]: Product };
export type ProductIdMapType = { [id: string]: Product };
export type ReviewsMapType = Map<string, Review[]>;

export const UID_BLACK_FRIDAY = 'black-friday';
export const UID_CHRISTMAS = 'christmas-deals';
export const UID_VALENTINES = 'valentines-special-offers';
export const UID_MOTHERS_DAY = 'mothers-day-special-offers';
export const UID_EASTER = 'easter-gifts-for-kids';
export const UID_FATHERS_DAY = 'fathers-day-special-offers';

// Instant Delivery source codes
export const DELIVERY_OPTIONS_EVOUCHER = [
  // Digital Delivery
  'MADMNE',
  // Digital Delivery + VGM
  'MADMVG',
];

// TODO LP-7094: use an interface and migrate off old Product API types
export type ProductChoicesListType = {
  slug: string;
  title: string;
  mainImageUrl: string;
  mainImageAltText: string;
  category?: {
    title: string;
    slug: string;
  };
};

export type Reviews = {
  averageRating: number;
  count: number;
};

export type Review = {
  title: string;
  sku: string;
  content: string;
  rating: number;
  reviewerName: string;
  dateCreated: string;
  reply?: string;
};

export type AdditionalDetails = {
  whatsIncluded: string;
  texts: InfoText[];
};

export type InfoText = {
  name: string;
  content: string;
};

export type Location = {
  name: string;
  region: string;
  lat: number;
  lng: number;
};

export type CategoryType = {
  hierarchy: string;
  id: number;
};

export type DeliveryOptions = {
  sourceCode: string;
  price: number;
  name: string;
  description: string;
};

export type Validity = {
  validityMonths: number;
  expiryDate: string;
};

export class Image {
  readonly url: string;
  readonly fallbackUrl?: string;
  readonly altText: string;
  readonly caption: string | undefined;

  constructor(apiImage: unknown) {
    this.url =
      typeof apiImage === 'object' &&
      apiImage !== null &&
      'Url' in apiImage &&
      typeof apiImage.Url === 'string'
        ? apiImage.Url
        : '';
    this.altText = 'not implemented';
  }
}

export type Video = {
  readonly videoId?: string;
};

export type Price = {
  rrp: number;
  currency: string;
  displayPrice: number;
  percentOff: number;
  fixedAmountOff: number;
};

export type SEO = {
  title: string;
  description: string;
};

export type ProductPage = {
  readonly id: number;
  readonly slug: string;
  readonly status: string;
  readonly categoryHierarchy: {
    lvl0: string;
    lvl1: string;
    lvl2: string;
  };
};

export type Facets = {
  features: string[];
  occasions: string[];
  featureHierarchies?: {
    lvl0?: string[];
  };
  occasionHierarchies?: {
    lvl0?: string[];
    lvl1?: string[];
  };
};

export type Product = {
  readonly reviews: Reviews;
  readonly locations: Location[];
  readonly sku: string;
  readonly promocode: string;
  readonly numberOfPeople: number;
  readonly id: number;
  readonly status: string;
  readonly title: string;
  readonly shortDescription: string;
  readonly description: string;
  readonly slug: string;
  readonly url: string;
  readonly media: { mainImage: Image; images: Image[]; video: Video };
  readonly price: Price;
  readonly category: string;
  readonly categories: string[];
  readonly info: AdditionalDetails;
  readonly categoryHierarchy: {
    lvl0: string;
    lvl1: string;
    lvl2: string;
  };
  readonly stock: number | null;
  readonly unitsAvailableIsPublic?: boolean;
  readonly seo: SEO;
  readonly searchIndex: number | null;
  readonly suppliers: { name: string }[];
  readonly deliveryOptions: DeliveryOptions[];
  readonly validity: Validity;
  readonly facets: Facets;
  readonly isCollectionProduct?: boolean;
  readonly isCollectionChoice?: boolean;
  readonly productChoices?: ProductChoicesListType[];
  readonly dateActivated: number;
  readonly isExclusive?: boolean;
  readonly isNew?: boolean;
  readonly isBundle?: boolean;
  readonly voucherPartTitles?: string[] | null;
  productPromo: ProductPromo | null; // not readonly as it is assigned dynamically
};
